import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { specraFocusRing } from './focus-ring';
import { SpecraSpinner } from './spinner';
/**
 * Specra Button — shadcn pattern with Specra brand rules.
 *
 * Brand rule: PRIMARY actions / CTAs are BLACK (`default`), not the blue
 * accent. `brand` is the blue variant — reserve it for the rare moment a
 * control must read as the interactive accent.
 *
 */ const specraButtonVariants = cva(`inline-flex items-center justify-center gap-[var(--space-1-5)] whitespace-nowrap rounded-[var(--radius-control)] font-medium transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)] ${specraFocusRing} disabled:pointer-events-none disabled:opacity-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0`, {
    variants: {
        variant: {
            default: 'border border-primary bg-primary text-primary-foreground hover:bg-primary/90',
            secondary: 'border border-border-strong bg-card text-foreground hover:bg-accent',
            ghost: 'border border-transparent text-foreground hover:bg-accent',
            outline: 'border border-border-strong bg-transparent text-foreground hover:bg-accent',
            brand: 'border border-brand bg-brand text-brand-foreground hover:bg-brand/90',
            destructive: 'border border-danger/30 bg-card text-danger hover:bg-danger-muted hover:text-danger',
            danger: 'border border-danger bg-danger text-danger-foreground hover:bg-danger/90',
            link: 'text-brand underline-offset-4 hover:underline'
        },
        size: {
            sm: 'h-[var(--control-height-sm)] px-[var(--space-2-5)] text-[length:var(--fs-ui-caption)]',
            default: 'h-[var(--control-height-md)] px-[var(--space-3-5)] text-[length:var(--fs-ui)]',
            lg: 'h-[var(--control-height-lg)] px-[var(--space-5)] text-[length:var(--fs-ui-title)]',
            icon: 'size-[var(--control-height-md)]',
            /** A compact chip-height control, e.g. a "[ source ]" provenance chip. */ xs: 'h-[var(--control-height-xs)] px-[var(--space-1-5)] text-[length:var(--fs-ui-meta)]',
            /**
         * Inline in running text or a table cell: no control height or padding,
         * inherits the surrounding font size. Pair with `variant="link"`.
         */ inline: 'h-auto p-0 align-baseline text-[length:inherit] leading-[inherit]'
        }
    },
    defaultVariants: {
        variant: 'default',
        size: 'default'
    }
});
const SpecraButton = /*#__PURE__*/ React.forwardRef(({ asChild = false, 'aria-busy': ariaBusy, 'aria-disabled': ariaDisabled, children, className, disabled, fullWidth = false, iconLeft, iconRight, isLoading = false, onClick, size, tabIndex, type = 'button', variant, ...props }, ref)=>{
    const Comp = asChild ? Slot : 'button';
    const slottedNativeButton = asChild && /*#__PURE__*/ React.isValidElement(children) && children.type === 'button';
    const unavailable = Boolean(disabled || isLoading);
    const blockActivation = (event)=>{
        event.preventDefault();
        event.stopPropagation();
    };
    const renderedChildren = asChild && /*#__PURE__*/ React.isValidElement(children) ? /*#__PURE__*/ React.cloneElement(children, {
        // Removing href closes middle-click, modifier-click, and context-menu
        // navigation paths that click cancellation alone cannot cover.
        ...unavailable ? {
            href: undefined,
            onAuxClick: blockActivation,
            onClick: blockActivation
        } : {},
        ...slottedNativeButton && !children.props.type ? {
            type
        } : {}
    }) : children;
    return /*#__PURE__*/ _jsxs(Comp, {
        ref: ref,
        className: cn(specraButtonVariants({
            variant,
            size
        }), fullWidth && 'w-full', className),
        "aria-busy": isLoading ? true : ariaBusy ?? undefined,
        "aria-disabled": asChild ? unavailable ? true : ariaDisabled ?? undefined : ariaDisabled,
        "data-disabled": asChild && unavailable ? '' : undefined,
        disabled: !asChild ? unavailable : undefined,
        onClick: (event)=>{
            if (unavailable) {
                blockActivation(event);
                return;
            }
            onClick?.(event);
        },
        tabIndex: asChild && unavailable ? -1 : tabIndex,
        ...!asChild ? {
            type
        } : {},
        ...props,
        children: [
            isLoading ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                className: "[border-color:color-mix(in_srgb,currentColor_30%,transparent)] [border-top-color:currentColor]",
                label: false,
                size: "sm"
            }) : null,
            !isLoading && iconLeft ? /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                children: iconLeft
            }) : null,
            /*#__PURE__*/ _jsx(Slottable, {
                children: renderedChildren
            }),
            !isLoading && iconRight ? /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                children: iconRight
            }) : null
        ]
    });
});
SpecraButton.displayName = 'SpecraButton';
export { SpecraButton, specraButtonVariants };
